import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Link, Modal, Text, Title } from '@nerdwallet/currency';
import InsuranceShield from './svg/InsuranceShield';
import SegmentImpression, {
  ImpressionType,
} from '../lib/segment/SegmentImpression';
import useAnalytics from '../lib/segment/useAnalytics';
import { SHOW_INSURANCE_PAINTED_DOOR_TEST_RESPONSE_STATUS } from '../auth/redux/constants';
import { type ReduxStore } from '../auth/redux/types';
import { INSURANCE_PAINTED_DOOR_TEST_URLS } from '../auth/lib/constants';
import localStorageHelper from '../auth/lib/localStorage';
import { SEGMENT_EVENT } from '@nerdwallet/js-auth-tools/dist/web/new-steps/constants';
import styles from './styles.module.less';

const analyticsBase = { section_name: 'efz_insurance_painted_door' };

const InsurancePaintedDoorTestModal: React.FC = () => {
  const segment = useAnalytics();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // The modal should only render on eligible URLs
  const isInsurancePaintedDoorTestUrl =
    INSURANCE_PAINTED_DOOR_TEST_URLS.includes(window?.location.pathname);

  // The modal should only render when the feature flag is turned on
  const isInsurancePaintedDoorTestModalActive = useSelector<
    ReduxStore,
    ReduxStore['insurancePaintedDoorTestModalActive']
  >((store) => store.insurancePaintedDoorTestModalActive);

  // Users should only view the modal a single time
  const hasUserViewedInsurancePaintedDoorTestModal =
    localStorageHelper.getItem('insurancePaintedDoorTestModalViewed') ===
    'true';

  useEffect(() => {
    if (
      isInsurancePaintedDoorTestUrl &&
      isInsurancePaintedDoorTestModalActive &&
      !hasUserViewedInsurancePaintedDoorTestModal
    ) {
      const timeoutId = setTimeout(() => {
        setIsModalOpen(true);
        localStorageHelper.setItem('insurancePaintedDoorTestModalViewed', true);
      }, 3000); // Delay rendering by 3 seconds

      return () => clearTimeout(timeoutId);
    }
  }, [
    isInsurancePaintedDoorTestUrl,
    isInsurancePaintedDoorTestModalActive,
    hasUserViewedInsurancePaintedDoorTestModal,
  ]);

  const dispatchShowInsurancePaintedDoorTestResponseStatus = useCallback(() => {
    dispatch({
      type: SHOW_INSURANCE_PAINTED_DOOR_TEST_RESPONSE_STATUS,
    });
  }, [dispatch]);

  const handleRequestClose = (e: any) => {
    e.preventDefault();

    segment.track(SEGMENT_EVENT.ELEMENT_INTERACTION, {
      entity_name: 'close_button',
      section_name: analyticsBase.section_name,
    });

    setIsModalOpen(false);
  };

  const handleClickCta = (e: any, isPrimary: boolean) => {
    e.preventDefault();

    segment.track(SEGMENT_EVENT.ELEMENT_INTERACTION, {
      entity_name: isPrimary ? 'primary_cta' : 'secondary_cta',
      section_name: analyticsBase.section_name,
    });

    dispatchShowInsurancePaintedDoorTestResponseStatus();

    setIsModalOpen(false);
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={handleRequestClose}
      width="640px"
    >
      <SegmentImpression
        eventName={SEGMENT_EVENT.ELEMENT_IMPRESSION}
        eventType={ImpressionType.TRACK}
        eventProps={{
          entity_name: 'efz_insurance_modal',
          section_name: analyticsBase.section_name,
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          className={styles.shieldContainer}
        >
          <InsuranceShield />
        </Box>
        <Box marginTop={{ mobile: '4', desktop: '3' }}>
          <Title size="small">A smarter way to save on insurance</Title>
        </Box>

        <Box marginTop={{ mobile: '2', desktop: '3' }}>
          <Text light>
            Rising premiums? NerdWallet could watch your rates and suggest ways
            to save - so it's easier to make smart decisions about insurance.
          </Text>
        </Box>

        <Box className={styles.ctaContainer}>
          <Button block primary onClick={(e) => handleClickCta(e, true)}>
            I'm interested
          </Button>
          <Box
            display="flex"
            justifyContent="center"
            marginTop={2}
            className={styles.secondaryCta}
          >
            <Link onClick={(e: any) => handleClickCta(e, false)}>
              Not interested
            </Link>
          </Box>
        </Box>
      </SegmentImpression>
    </Modal>
  );
};

export default InsurancePaintedDoorTestModal;
